import { HttpClient } from "@angular/common/http";
import { inject, Injectable, signal } from "@angular/core";
import { environment } from "../../../environments/environment";
import { Observable, tap } from "rxjs";
import { PageResult } from "../../core/models/page-result.model";
import { NotificationGetModel } from "../models/notification-get.model";
import { NotificationPostModel } from "../models/notification-post.model";

@Injectable({
    providedIn: "root"
})
export class NotificationApiService {
    private readonly http = inject(HttpClient);
    private readonly baseUrl = `${environment.apiUrl}/notification`;

    // State for unread notifications
    public unreadNotifications = signal<NotificationGetModel[]>([]);

    // State for pinned notifications (persistent banner) — visible regardless of seen state until expiry.
    public pinnedNotifications = signal<NotificationGetModel[]>([]);

    public getNotifications(page: number = 0, size: number = 10): Observable<PageResult<NotificationGetModel>> {
        return this.http.get<PageResult<NotificationGetModel>>(`${this.baseUrl}?page=${page}&size=${size}`);
    }

    public getUnreadNotifications(): Observable<NotificationGetModel[]> {
        return this.http.get<NotificationGetModel[]>(`${this.baseUrl}/unread`).pipe(
            tap(notifications => this.unreadNotifications.set(notifications))
        );
    }

    public getPinnedNotifications(): Observable<NotificationGetModel[]> {
        return this.http.get<NotificationGetModel[]>(`${this.baseUrl}/pinned`).pipe(
            tap(notifications => this.pinnedNotifications.set(notifications))
        );
    }

    public getAllNotifications(page: number = 0, size: number = 10): Observable<PageResult<NotificationGetModel>> {
        return this.http.get<PageResult<NotificationGetModel>>(`${this.baseUrl}/all?page=${page}&size=${size}`);
    }

    public markNotificationAsSeen(notificationId: String): Observable<NotificationGetModel> {
        return this.http.patch<NotificationGetModel>(`${this.baseUrl}/${notificationId}/read`, {}).pipe(
            tap(updatedNotification => {
                // Update the unread notifications list by removing the marked notification
                const currentNotifications = this.unreadNotifications();
                const filteredNotifications = currentNotifications.filter(n => n.id !== notificationId);
                this.unreadNotifications.set(filteredNotifications);
            })
        );
    }

    public createNotification(notification: NotificationPostModel): Observable<NotificationGetModel> {
        return this.http.post<NotificationGetModel>(`${this.baseUrl}`, notification);
    }

    public deleteNotification(notificationId: String): Observable<void> {
        return this.http.delete<void>(`${this.baseUrl}/${notificationId}`);
    }
}